import React, { memo, useEffect, useState } from 'react';
import { Options } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import { CHART_HEIGHT_PIXELS, getChartEvents, getCreditsStyle } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';

export interface BubbleDataPoint {
    x: number;
    y: number;
    z: number; // bubble size
    name?: string;
    color?: string;
    score?: number;
    rag?: string;
}

export interface BubbleSeries {
    name: string;
    data: BubbleDataPoint[];
}

export interface BubbleChartProps {
    title?: string;
    panelData: BubbleSeries[];
    xAxisTitle?: string;
    yAxisTitle?: string;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    showLegend?: boolean;
    theme?: PanelTheme;
    showWaterMark?: boolean;
    tooltipFormatter?: (this: Highcharts.Point, xAxisTitle?: string, yAxisTitle?: string) => string;
    customOptions?: Options;
    customHeight?: number;
}

function buildPointFormatter(
    xAxisTitle?: string,
    yAxisTitle?: string,
    tooltipFormatter?: BubbleChartProps['tooltipFormatter'],
): (this: Highcharts.Point) => string {
    return function pointFormatter(this: Highcharts.Point) {
        if (tooltipFormatter) {
            return tooltipFormatter.call(this, xAxisTitle, yAxisTitle);
        }
        return `<b>${this.name ?? ''}</b>`;
    };
}

const Chart = (props: BubbleChartProps) => {
    const {
        panelData,
        title,
        xAxisTitle,
        yAxisTitle,
        showLegend,
        theme = PanelTheme.NONE,
        showWaterMark = false,
        chartRef,
        tooltipFormatter,
        customOptions,
        customHeight,
    } = props;

    const [options, setOptions] = useState<Options | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        setOptions(
            Highcharts.merge(
                {
                    colors: getThemeColours(theme),
                    credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
                    chart: {
                        type: 'bubble',
                        height: customHeight ?? CHART_HEIGHT_PIXELS,
                        events: getChartEvents(false, false, title, undefined, {
                            fullScreenOptions: {
                                title: { text: title, style: { fontWeight: '600' } },
                            },
                            printOptions: {
                                title: { text: title, style: { fontWeight: '500' } },
                            },
                        }),
                    },
                    exporting: {
                        chartOptions: {
                            title: { text: title, style: { fontWeight: 'semi-bold' } },
                            credits: { enabled: false },
                        },
                        enabled: true,
                        fallbackToExportServer: false,
                        buttons: {
                            contextButton: {
                                enabled: false,
                            },
                        },
                    },
                    title: {
                        text: undefined,
                    },
                    xAxis: {
                        title: {
                            text: xAxisTitle,
                        },
                        gridLineWidth: 0,
                        allowDecimals: false,
                    },
                    yAxis: {
                        title: {
                            text: yAxisTitle,
                        },
                        gridLineWidth: 0,
                        allowDecimals: false,
                    },
                    legend: {
                        enabled: showLegend,
                        itemStyle: {
                            cursor: 'pointer',
                            textOverflow: 'ellipsis',
                        },
                        layout: 'vertical',
                        align: 'right',
                        maxHeight: 200,
                        verticalAlign: 'middle',
                        width: 200,
                    },
                    tooltip: {
                        useHTML: true,
                        headerFormat: '',
                        pointFormatter: buildPointFormatter(xAxisTitle, yAxisTitle, tooltipFormatter),
                    },
                    plotOptions: {
                        bubble: {
                            minSize: 10,
                            maxSize: 60,
                            dataLabels: {
                                enabled: false,
                            },
                        },
                    },
                    series: panelData.map((s) => ({
                        type: 'bubble' as const,
                        name: s.name,
                        data: s.data.map((pt) => ({
                            x: pt.x,
                            y: pt.y,
                            z: pt.z,
                            name: pt.name,
                            color: pt.color,
                            score: pt.score,
                            rag: pt.rag,
                        })),
                    })),
                },
                customOptions,
            ),
        );
    }, [
        panelData,
        title,
        xAxisTitle,
        yAxisTitle,
        showLegend,
        theme,
        showWaterMark,
        chartRef,
        tooltipFormatter,
        t,
        customOptions,
    ]);

    if (options === null) return <Loading />;

    return <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />;
};

export const BubbleChart = memo(Chart);
